
import { supabase } from "@/integrations/supabase/client";
import { Movie } from "@/types/movie";
import { AuthError } from "../errors/authErrors";
import { logNetwork } from "@/utils/debugLogger";
import { fromDbRow } from "@/utils/fromDbRow";

/**
 * Get all movies for the current user with enhanced metadata support
 * UPDATED: Now uses get_user_movies_with_metadata RPC for future-proofed data retrieval
 */
export async function getUserMovies(): Promise<Movie[]> {
  try {
    console.log("🔍 [GET-MOVIES] Starting getUserMovies with enhanced metadata support");
    
    // Step 1: Check authentication with detailed logging
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    console.log("🔍 [GET-MOVIES] Auth check result:", {
      hasUser: !!user,
      userId: user?.id || 'none',
      authError: authError?.message || 'none',
      userEmail: user?.email || 'none'
    });
    
    if (authError) {
      console.error("❌ [GET-MOVIES] Authentication error:", {
        message: authError.message,
        code: authError.code || 'unknown'
      });
      throw new AuthError("Authentication error: " + authError.message);
    }
    
    if (!user) {
      console.log("⚠️ [GET-MOVIES] No authenticated user found, returning empty array");
      logNetwork("No authenticated user, returning empty array");
      return [];
    }
    
    console.log(`✅ [GET-MOVIES] User authenticated successfully: ${user.id}`);
    
    // Step 2: Use enhanced RPC function to get movies with full metadata
    console.log("🔄 [GET-MOVIES] Executing enhanced metadata query...");
    const startTime = Date.now();
    
    const { data, error } = await supabase.rpc('get_user_movies_with_metadata', {
      p_user_id: user.id
    });
    
    const queryDuration = Date.now() - startTime;
    console.log(`⏱️ [GET-MOVIES] Enhanced query completed in ${queryDuration}ms`);
    
    if (error) {
      console.error('❌ [GET-MOVIES] Enhanced query error:', {
        message: error.message,
        code: error.code,
        hint: error.hint,
        queryDuration
      });
      
      // Fallback to basic query if enhanced query fails
      console.log("🔄 [GET-MOVIES] Falling back to basic user_movies query...");
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('user_movies')
        .select(`
          user_id,
          tmdb_id,
          title,
          director,
          year,
          tags,
          favorite,
          plan_to_watch,
          poster_path,
          backdrop_path,
          overview,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      
      if (fallbackError) {
        console.error('❌ [GET-MOVIES] Fallback query also failed:', fallbackError);
        throw fallbackError;
      }
      
      console.log(`📊 [GET-MOVIES] Fallback query result:`, {
        rowCount: fallbackData?.length || 0,
        hasData: !!fallbackData
      });
      
      if (!fallbackData || fallbackData.length === 0) {
        console.log("📭 [GET-MOVIES] No movies found for user (fallback)");
        return [];
      }
      
      // Transform fallback data
      const movies = fallbackData.map(row => fromDbRow(row));
      console.log(`✅ [GET-MOVIES] Successfully processed ${movies.length} movies (fallback)`);
      return movies;
    }
    
    console.log(`📊 [GET-MOVIES] Enhanced query result:`, {
      rowCount: data?.length || 0,
      hasData: !!data,
      firstRowSample: data?.[0] ? {
        tmdb_id: data[0].tmdb_id,
        title: data[0].title,
        favorite: data[0].favorite,
        plan_to_watch: data[0].plan_to_watch,
        vote_average: data[0].vote_average,
        runtime: data[0].runtime,
        release_date: data[0].release_date
      } : 'no data'
    });
    
    if (!data || data.length === 0) {
      console.log("📭 [GET-MOVIES] No movies found for user");
      logNetwork("No movies found for authenticated user");
      return [];
    }
    
    // Step 3: Transform enhanced data with full metadata
    console.log("🔄 [GET-MOVIES] Transforming enhanced database rows to Movie objects...");
    const transformStartTime = Date.now();
    
    const movies: Movie[] = [];
    const invalidRows: any[] = [];
    
    data.forEach((row, index) => {
      try {
        // Validate required fields
        if (!row.tmdb_id) {
          console.warn(`⚠️ [GET-MOVIES] Row ${index} missing tmdb_id:`, row);
          invalidRows.push({ index, reason: 'missing tmdb_id', row });
          return;
        }
        
        // Enhanced transformation with full metadata
        const movie: Movie = {
          tmdbId: row.tmdb_id,
          title: row.title || 'Unknown Title',
          director: row.director || 'Unknown Director', 
          year: row.year || 0,
          tags: row.tags || [],
          favorite: row.favorite || false,
          planToWatch: row.plan_to_watch || false,
          posterPath: row.poster_path,
          backdropPath: row.backdrop_path,
          overview: row.overview,
          // Enhanced metadata fields from the new query
          releaseDate: row.release_date,
          voteAverage: row.vote_average,
          runtime: row.runtime,
          popularity: row.popularity,
          genres: row.tags || [], // Using tags as genres for now
          source: 'database' as const
        };
        
        movies.push(movie);
        
        // Log first few transformations for debugging
        if (index < 3) {
          console.log(`🔄 [GET-MOVIES] Enhanced transformation row ${index}:`, {
            tmdbId: movie.tmdbId,
            title: movie.title,
            favorite: movie.favorite,
            planToWatch: movie.planToWatch,
            voteAverage: movie.voteAverage,
            runtime: movie.runtime,
            releaseDate: movie.releaseDate
          });
        }
      } catch (transformError) {
        console.error(`❌ [GET-MOVIES] Error transforming enhanced row ${index}:`, {
          error: transformError,
          row
        });
        invalidRows.push({ index, reason: 'transform error', error: transformError, row });
      }
    });
    
    const transformDuration = Date.now() - transformStartTime;
    console.log(`⏱️ [GET-MOVIES] Enhanced transformation completed in ${transformDuration}ms`);
    
    if (invalidRows.length > 0) {
      console.warn(`⚠️ [GET-MOVIES] Found ${invalidRows.length} invalid rows:`, invalidRows);
    }
    
    console.log(`✅ [GET-MOVIES] Successfully processed ${movies.length} movies with enhanced metadata`, {
      totalRows: data.length,
      validMovies: movies.length,
      invalidRows: invalidRows.length,
      favoriteCount: movies.filter(m => m.favorite).length,
      planToWatchCount: movies.filter(m => m.planToWatch).length,
      withVoteAverage: movies.filter(m => m.voteAverage).length,
      withRuntime: movies.filter(m => m.runtime).length,
      withReleaseDate: movies.filter(m => m.releaseDate).length
    });
    
    logNetwork(`Successfully retrieved ${movies.length} movies with enhanced metadata for user`);
    return movies;
    
  } catch (error) {
    console.error('❌ [GET-MOVIES] Unexpected error:', {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      type: error?.constructor?.name || 'unknown'
    });
    
    if (error instanceof AuthError) {
      console.log("🔒 [GET-MOVIES] Auth error, returning empty array");
      logNetwork("Auth error, returning empty array");
      return [];
    }
    
    // Don't throw errors for data loading - return empty array instead
    console.log("🔄 [GET-MOVIES] Returning empty array due to error");
    return [];
  }
}
